import React, { useState, useRef, useEffect } from "react";
import MoviePoster from "./MoviePoster";
import LongPressPreview from "./LongPressPreview";
import api from "../lib/api";

/** Beautiful branded fallback when YouTube thumbnails fail.
 *  Renders a real human-face photo (pravatar.cc — Unsplash portraits)
 *  seeded by title hash so every empty card shows an actual face
 *  instead of letters/symbols. Movies still get the purpose-built
 *  poster card. */
export function BrandedThumb({ title, subtitle, kind = "music", className = "" }) {
  // Movies get their own purpose-built poster card — proper film
  // typography + director credit + year — instead of the photo tile.
  if (kind === "movie" || kind === "film") {
    return <MoviePoster title={title} director={subtitle} className={className} />;
  }
  // Deterministic face index 1-70 (pravatar.cc range) seeded by
  // title+subtitle so the same content always shows the same person.
  const seed = Math.abs([...((title || "") + (subtitle || "Holy Words"))].reduce((a, c) => a + c.charCodeAt(0), 0));
  const faceIdx = (seed % 70) + 1;
  return (
    <div className={`absolute inset-0 w-full h-full overflow-hidden bg-ink ${className}`}>
      <img
        src={`https://i.pravatar.cc/640?img=${faceIdx}`}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
    </div>
  );
}

// Module-level guard so only ONE hover-preview iframe is active across
// the page at a time. Hovering a new card cancels any preview on the
// previously-hovered one. Prevents 20 YouTube iframes loading at once
// when the user sweeps the mouse down a grid.
let __hwActivePreviewSetter = null;

/** Thumbnail image that automatically swaps to a branded card if the
 *  YouTube image 404s OR returns the "deleted video" gray placeholder
 *  (YouTube serves it as a 120x90 image, while real thumbs are 480x360+).
 *  Optionally notifies the parent via onUnavailable so the player can
 *  fall back to a search-based embed for the same track.
 *
 *  Hover preview (YouTube-style desktop): hovering a card for ~550ms
 *  swaps the static thumbnail for a muted autoplay iframe playing
 *  seconds 12–22 of the source video. Disabled on touch devices.
 *
 *  Long-press preview (mobile + desktop): pressing and holding any
 *  card for ~650ms opens a fullscreen pinned preview overlay with
 *  title, subtitle, and a "Watch full" CTA. Suppresses the synthetic
 *  click that would otherwise fire when the user releases, so the
 *  parent button's `onClick={handleWatch}` doesn't open the full
 *  player at the same time. */
export default function MediaThumb({ youtube_id, poster_url, poster, title, subtitle, kind = "music", onUnavailable }) {
  const [failed, setFailed] = useState(false);
  const [thumbVariantIdx, setThumbVariantIdx] = useState(0);
  // iTunes album art resolved lazily once the YouTube fallback chain
  // is exhausted (or when there's no youtube_id to begin with). The
  // backend caches by (artist, title) so repeat hits are free. Only
  // attempted for music-kind tiles — sermons / cartoons get the
  // BrandedThumb fallback instead.
  const [iTunesArt, setITunesArt] = useState(null);
  const [iTunesTried, setITunesTried] = useState(false);
  // YouTube thumbnail fallback chain. mqdefault returns 120×90 grey
  // placeholder for unavailable videos — we treat that as failure and
  // try hqdefault next (different CDN path; sometimes Mark Lowry /
  // Kountry Wayne clips have only hq), then maxresdefault, then
  // sddefault. Only after all four miss do we drop to the branded
  // music-note fallback. User reported: "every single movie cartoon
  // video gospel comedy sermon they all need to have thumbnails".
  const THUMB_VARIANTS = [
    "mqdefault.jpg",   // 320×180 (most uploads)
    "hqdefault.jpg",   // 480×360 (legacy uploads)
    "maxresdefault.jpg", // 1280×720 (HD uploads)
    "sddefault.jpg",   // 640×480 (older HD uploads)
  ];
  const [previewing, setPreviewing] = useState(false);
  const [pinnedPreview, setPinnedPreview] = useState(false);
  const hoverTimerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const pointerStartRef = useRef({ x: 0, y: 0 });
  const longPressFiredRef = useRef(false);
  const wrapperRef = useRef(null);
  const markDead = () => {
    setFailed(true);
    if (onUnavailable && youtube_id) onUnavailable(youtube_id);
  };
  // Accept both `poster_url` (canonical) and `poster` (legacy alias —
  // movie data files use `poster`) so a single call site can pass either.
  const explicitPoster = poster_url || poster;

  // Only enable hover-preview on devices that actually have a hover
  // capability (desktop / trackpad). Mobile browsers report `hover:none`
  // and `pointer:coarse`. matchMedia is the cleanest check.
  const canHover = (() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  })();

  const stopPreview = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setPreviewing(false);
    if (__hwActivePreviewSetter === setPreviewing) __hwActivePreviewSetter = null;
  };

  const startPreview = () => {
    if (!canHover || !youtube_id || failed) return;
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      // Cancel any other card's preview before starting our own.
      if (__hwActivePreviewSetter && __hwActivePreviewSetter !== setPreviewing) {
        __hwActivePreviewSetter(false);
      }
      __hwActivePreviewSetter = setPreviewing;
      setPreviewing(true);
    }, 550);
  };

  // ───────── Long-press (mobile + desktop) ─────────
  // We attach pointerdown/up/move/cancel on the thumb wrapper. A
  // long-press is "pointer held for ≥650ms without moving more than
  // 12px". When that fires we open the fullscreen pinned preview AND
  // install a one-shot capture-phase click listener on `document` to
  // swallow the click that's about to bubble up to the parent button.
  // This prevents the underlying card's `onClick={handleWatch}` from
  // ALSO firing on release — without modifying any parent component.
  const cancelLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Stable suppressor ref so we can register on pointerdown and
  // remove deterministically on pointerup (without long-press) or via
  // its own `once: true` capture firing.
  const clickSuppressorRef = useRef(null);

  const onPointerDown = (e) => {
    if (!youtube_id || failed) return;
    longPressFiredRef.current = false;
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    cancelLongPressTimer();
    // Register the click-suppressor IMMEDIATELY (not inside the
    // 650ms timer) so that even on slow devices where the timer
    // fires late, the synthetic click from pointerup is still
    // caught at capture phase before the parent button's onClick
    // can run. We arm it but only swallow if longPressFiredRef is
    // true at click time — so a quick tap (no long-press) passes
    // through normally.
    const suppress = (ev) => {
      if (!longPressFiredRef.current) return; // quick tap → let click through
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
    };
    clickSuppressorRef.current = suppress;
    document.addEventListener("click", suppress, true);
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setPinnedPreview(true);
      // Also stop any hover preview so we don't render two iframes.
      stopPreview();
    }, 650);
  };

  const removeClickSuppressor = () => {
    if (clickSuppressorRef.current) {
      try { document.removeEventListener("click", clickSuppressorRef.current, true); } catch (_) {}
      clickSuppressorRef.current = null;
    }
  };

  const onPointerMove = (e) => {
    if (!longPressTimerRef.current) return;
    const dx = (e.clientX || 0) - pointerStartRef.current.x;
    const dy = (e.clientY || 0) - pointerStartRef.current.y;
    if (dx * dx + dy * dy > 144) {
      cancelLongPressTimer();
      removeClickSuppressor();
    }
  };

  const onPointerEndOrCancel = () => {
    cancelLongPressTimer();
    // Allow click through naturally if no long-press fired. The
    // suppressor checks longPressFiredRef — clearing the listener
    // after a short delay keeps it harmless either way.
    setTimeout(() => removeClickSuppressor(), 400);
  };

  useEffect(() => () => {
    stopPreview();
    cancelLongPressTimer();
    removeClickSuppressor();
  }, []);

  // Trigger iTunes lookup for music tiles when we land in the
  // "failed" state (no usable YouTube thumb) and don't have an
  // explicit poster. Movies / cartoons / sermons stay on the
  // BrandedThumb path. Idempotent — we only fire once per mount.
  useEffect(() => {
    if (iTunesTried) return;
    const isMusicKind = kind === "music";
    const hasYouTube = !!youtube_id && !failed;
    if (!isMusicKind || hasYouTube || explicitPoster) return;
    if (!title && !subtitle) return;
    setITunesTried(true);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/media/artwork", {
          params: { artist: subtitle || "", title: title || "" },
        });
        if (!cancelled && data?.url) setITunesArt(data.url);
      } catch { /* keep BrandedThumb fallback */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed, youtube_id, kind, title, subtitle]);

  const thumbInner = (() => {
    if (explicitPoster && !failed) {
      return (
        <img
          src={explicitPoster}
          onError={() => setFailed(true)}
          alt={title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      );
    }
    if (failed || !youtube_id) {
      // Real album art beats the branded initials tile every time.
      if (iTunesArt) {
        return (
          <img
            src={iTunesArt}
            onError={() => setITunesArt(null)}
            alt={title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        );
      }
      return <BrandedThumb title={title} subtitle={subtitle} kind={kind} />;
    }
    return (
      <img
        // Fallback chain — see THUMB_VARIANTS at component top.
        src={`https://i.ytimg.com/vi/${youtube_id}/${THUMB_VARIANTS[thumbVariantIdx]}`}
        loading="lazy"
        decoding="async"
        onError={() => {
          if (thumbVariantIdx + 1 < THUMB_VARIANTS.length) {
            setThumbVariantIdx(thumbVariantIdx + 1);
          } else {
            markDead();
          }
        }}
        onLoad={(e) => {
          // 120×90 = YouTube's grey "unavailable" placeholder. Try the
          // next variant before declaring the thumbnail dead.
          if (e.currentTarget.naturalWidth <= 120) {
            if (thumbVariantIdx + 1 < THUMB_VARIANTS.length) {
              setThumbVariantIdx(thumbVariantIdx + 1);
            } else {
              markDead();
            }
          }
        }}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
    );
  })();

  return (
    <>
      <div
        ref={wrapperRef}
        className="absolute inset-0"
        onMouseEnter={startPreview}
        onMouseLeave={stopPreview}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerEndOrCancel}
        onPointerCancel={onPointerEndOrCancel}
        onPointerMove={onPointerMove}
        style={{ touchAction: "manipulation" }}
        data-testid="media-thumb"
      >
        {thumbInner}
        {previewing && youtube_id && !pinnedPreview && (
          <iframe
            title={`${title} preview`}
            src={`https://www.youtube.com/embed/${youtube_id}?autoplay=1&mute=1&controls=0&modestbranding=1&playsinline=1&rel=0&start=12&end=22&loop=1&playlist=${youtube_id}`}
            className="absolute inset-0 w-full h-full pointer-events-none"
            allow="autoplay; encrypted-media"
            frameBorder="0"
            data-testid="media-thumb-preview"
          />
        )}
      </div>
      {pinnedPreview && (
        <LongPressPreview
          youtube_id={youtube_id}
          title={title}
          subtitle={subtitle}
          poster={explicitPoster}
          onClose={() => setPinnedPreview(false)}
          onPlayFull={() => {
            // Synthesise a click on THIS thumb's parent card button so
            // the user gets taken into the full player. Walk up from
            // our own wrapper ref (not a global query — there are
            // dozens of MediaThumb instances on a page).
            setPinnedPreview(false);
            setTimeout(() => {
              try {
                const btn = wrapperRef.current ? wrapperRef.current.closest("button") : null;
                if (btn) btn.click();
              } catch (_) {}
            }, 50);
          }}
        />
      )}
    </>
  );
}
